// src/pages/BookDetail.jsx - Halaman Detail Buku Lengkap
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { jwtDecode } from 'jwt-decode';
import LayoutUser from '../components/layoutUser';
import { 
    FaBook, FaUserEdit, FaCalendarAlt, FaClone, FaBookmark, 
    FaHashtag, FaBarcode, FaMapMarkerAlt, FaMoneyBillWave,
    FaCopyright, FaInfoCircle, FaArrowLeft, FaCheckCircle,
    FaTimesCircle
} from 'react-icons/fa';

const BookDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [book, setBook] = useState(null);
    const [relatedBooks, setRelatedBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Set tanggal default untuk form
    const today = new Date();
    const defaultBorrowDate = today.toISOString().slice(0, 10);
    const returnDate = new Date(today);
    returnDate.setDate(today.getDate() + 7);
    const defaultReturnDate = returnDate.toISOString().slice(0, 10);

    const [formData, setFormData] = useState({
        tanggal_peminjaman: defaultBorrowDate,
        tanggal_kembali: defaultReturnDate,
    });

    useEffect(() => {
        fetchBookDetail();
    }, [id]);

    const fetchBookDetail = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = { 'x-auth-token': token };

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/buku/${id}`, { headers });
            setBook(response.data);

            // Fetch related books (same category)
            if (response.data.kategori_list && response.data.kategori_list.length > 0) {
                fetchRelatedBooks(response.data.kategori_list[0]);
            }

            setLoading(false);
        } catch (error) {
            console.error('Error fetching book detail:', error);
            Swal.fire('Error', 'Gagal memuat detail buku', 'error');
            setLoading(false);
        }
    };

    const fetchRelatedBooks = async (categoryId) => {
        try {
            const token = localStorage.getItem('token');
            const headers = { 'x-auth-token': token };
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/buku`, { headers });
            
            const filtered = response.data
                .filter(b => 
                    b.id !== parseInt(id) && 
                    b.kategori_list && 
                    b.kategori_list.includes(categoryId)
                )
                .slice(0, 4);
            
            setRelatedBooks(filtered);
        } catch (error) {
            console.error('Error fetching related books:', error);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleBorrowClick = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                Swal.fire('Gagal', 'Anda harus login untuk meminjam buku.', 'error');
                return;
            }

            const decoded = jwtDecode(token);
            const id_anggota = decoded.id;

            const headers = { 'x-auth-token': token };
            const data = {
                id_anggota,
                id_buku: book.id,
                ...formData
            };

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/peminjaman`, data, { headers });

            Swal.fire({
                title: 'Pengajuan Berhasil!',
                html: `
                    <div style="text-align: left; padding: 10px;">
                        <p style="margin-bottom: 10px;">${response.data.message}</p>
                        <div style="background-color: #fff3cd; padding: 12px; border-radius: 6px; border-left: 4px solid #ffc107;">
                            <p style="margin: 0; font-size: 14px; color: #856404;">
                                <strong>⏱️ Status:</strong> Menunggu Persetujuan Admin<br>
                                <strong>⚠️ Perhatian:</strong> Pengajuan akan hangus dalam 24 jam jika tidak disetujui
                            </p>
                        </div>
                    </div>
                `,
                icon: 'success',
                confirmButtonText: 'OK',
                confirmButtonColor: '#3273dc'
            });

            closeModal();
            fetchBookDetail(); // Refresh data

        } catch (error) {
            console.error('Error during borrowing:', error.response || error);
            const errorMessage = error.response?.data?.message || 
                               'Gagal mengajukan peminjaman buku. Silakan coba lagi.';
            
            Swal.fire({
                title: 'Gagal',
                text: errorMessage,
                icon: 'error',
                confirmButtonColor: '#3273dc'
            });
        }
    };

    const getUniqueKategori = () => {
        if (!book?.nama_kategori) return '-';
        const kategoris = book.nama_kategori.split(',').map(k => k.trim()).filter(k => k);
        return [...new Set(kategoris)].join(', ');
    };

    const InfoRow = ({ icon, label, value, isCurrency = false }) => (
        <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            padding: isMobile ? '10px 0' : '12px 0',
            borderBottom: '1px solid #f0f0f0'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                minWidth: isMobile ? '120px' : '180px',
                color: '#666',
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: '500'
            }}>
                {React.cloneElement(icon, {
                    style: { marginRight: '8px', color: '#3273dc', fontSize: isMobile ? '14px' : '16px' }
                })}
                {label}
            </div>
            <div style={{
                flex: 1,
                color: '#333',
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: isCurrency ? '600' : '400',
                color: isCurrency ? '#28a745' : '#333'
            }}>
                {value || '-'}
            </div>
        </div>
    );

    const formatRupiah = (value) => {
        if (!value) return '-';
        return 'Rp ' + parseInt(value).toLocaleString('id-ID');
    };

    if (loading) {
        return (
            <LayoutUser>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <p>Memuat detail buku...</p>
                </div>
            </LayoutUser>
        );
    }

    if (!book) {
        return (
            <LayoutUser>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <p>Buku tidak ditemukan</p>
                </div>
            </LayoutUser>
        );
    }

    // ✅ FIXED: Cek stok dengan lebih teliti
    const isAvailable = book.stok_tersedia !== undefined && book.stok_tersedia !== null && book.stok_tersedia > 0;
    
    // Debug log
    console.log('Book availability check:', {
        stok_tersedia: book.stok_tersedia,
        jumlah_buku: book.jumlah_buku,
        isAvailable: isAvailable
    });

    return (
        <LayoutUser>
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
                padding: isMobile ? '15px' : '20px'
            }}>
                {/* Back Button */}
                <button
                    onClick={() => navigate(-1)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: isMobile ? '8px 12px' : '10px 16px',
                        marginBottom: '20px',
                        border: '1px solid #ddd',
                        backgroundColor: '#fff',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: isMobile ? '13px' : '14px',
                        transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f8f9fa';
                        e.target.style.borderColor = '#3273dc';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#fff';
                        e.target.style.borderColor = '#ddd';
                    }}
                >
                    <FaArrowLeft /> Kembali
                </button>

                {/* Main Content */}
                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '20px' : '30px',
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    padding: isMobile ? '20px' : '30px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                    {/* Book Image */}
                    <div style={{
                        flexShrink: 0,
                        width: isMobile ? '100%' : '300px'
                    }}>
                        <div style={{
                            width: '100%',
                            height: isMobile ? '350px' : '400px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }}>
                            {book.foto_buku ? (
                                <img
                                    src={`${process.env.REACT_APP_BASE_URL}/images/buku/${book.foto_buku}`}
                                    alt={book.judul}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            ) : (
                                <FaBook style={{ fontSize: '100px', color: '#ccc' }} />
                            )}
                        </div>

                        {/* Availability Status */}
                        <div style={{
                            marginTop: '20px',
                            padding: '15px',
                            backgroundColor: isAvailable ? '#e6f4ea' : '#fce8e6',
                            borderRadius: '8px',
                            textAlign: 'center'
                        }}>
                            {isAvailable ? (
                                <>
                                    <FaCheckCircle style={{ color: '#1e4620', fontSize: '24px', marginBottom: '8px' }} />
                                    <p style={{ margin: 0, color: '#1e4620', fontWeight: 'bold', fontSize: '16px' }}>
                                        Tersedia
                                    </p>
                                    <p style={{ margin: '5px 0 0 0', color: '#1e4620', fontSize: '14px' }}>
                                        Stok: {book.stok_tersedia} buku
                                    </p>
                                </>
                            ) : (
                                <>
                                    <FaTimesCircle style={{ color: '#c5221f', fontSize: '24px', marginBottom: '8px' }} />
                                    <p style={{ margin: 0, color: '#c5221f', fontWeight: 'bold', fontSize: '16px' }}>
                                        Tidak Tersedia
                                    </p>
                                </>
                            )}
                        </div>

                        {/* Borrow Button */}
                        <button
                            onClick={handleBorrowClick}
                            disabled={!isAvailable}
                            style={{
                                width: '100%',
                                marginTop: '15px',
                                padding: '14px',
                                border: 'none',
                                backgroundColor: isAvailable ? '#3273dc' : '#ccc',
                                color: '#fff',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                cursor: isAvailable ? 'pointer' : 'not-allowed',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                if (isAvailable) e.target.style.backgroundColor = '#2366d1';
                            }}
                            onMouseLeave={(e) => {
                                if (isAvailable) e.target.style.backgroundColor = '#3273dc';
                            }}
                        >
                            {isAvailable ? 'Pinjam Buku Ini' : 'Tidak Tersedia'}
                        </button>
                    </div>

                    {/* Book Details */}
                    <div style={{ flex: 1 }}>
                        <h1 style={{
                            margin: '0 0 10px 0',
                            fontSize: isMobile ? '22px' : '28px',
                            fontWeight: 'bold',
                            color: '#333',
                            lineHeight: '1.3'
                        }}>
                            {book.judul}
                        </h1>

                        {book.pengarang && (
                            <p style={{
                                margin: '0 0 20px 0',
                                fontSize: isMobile ? '15px' : '17px',
                                color: '#666',
                                fontStyle: 'italic'
                            }}>
                                oleh {book.pengarang}
                            </p>
                        )}

                        {/* Detail Information */}
                        <div style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            padding: isMobile ? '15px' : '20px',
                            marginTop: '20px'
                        }}>
                            <h3 style={{
                                margin: '0 0 15px 0',
                                fontSize: isMobile ? '16px' : '18px',
                                fontWeight: 'bold',
                                color: '#333',
                                borderBottom: '2px solid #3273dc',
                                paddingBottom: '8px'
                            }}>
                                Informasi Buku
                            </h3>

                            <InfoRow icon={<FaBarcode />} label="No. Induk" value={book.no_induk_buku} />
                            <InfoRow icon={<FaHashtag />} label="Kategori" value={getUniqueKategori()} />
                            <InfoRow icon={<FaCalendarAlt />} label="Tahun Terbit" value={book.tahun_terbit} />
                            <InfoRow icon={<FaClone />} label="Edisi" value={book.edisi} />
                            <InfoRow icon={<FaBookmark />} label="Penerbit" value={book.penerbit} />
                            <InfoRow icon={<FaBook />} label="Cetakan" value={book.cetakan} />
                            <InfoRow icon={<FaCopyright />} label="Copyright" value={book.copyright} />
                            <InfoRow icon={<FaMapMarkerAlt />} label="Rak Buku" value={book.rak_buku} />
                            <InfoRow icon={<FaBook />} label="Asal Buku" value={book.asal_buku} />
                            <InfoRow icon={<FaMoneyBillWave />} label="Harga" value={formatRupiah(book.harga)} isCurrency />
                            <InfoRow icon={<FaBook />} label="Total Stok" value={book.jumlah_buku} />
                        </div>

                        {/* Additional Info */}
                        {book.keterangan && (
                            <div style={{
                                marginTop: '20px',
                                padding: isMobile ? '15px' : '20px',
                                backgroundColor: '#e7f3ff',
                                borderRadius: '8px',
                                borderLeft: '4px solid #3273dc'
                            }}>
                                <h3 style={{
                                    margin: '0 0 10px 0',
                                    fontSize: isMobile ? '14px' : '16px',
                                    fontWeight: 'bold',
                                    color: '#333',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <FaInfoCircle style={{ color: '#3273dc' }} />
                                    Keterangan
                                </h3>
                                <p style={{
                                    margin: 0,
                                    fontSize: isMobile ? '13px' : '14px',
                                    color: '#666',
                                    lineHeight: '1.6'
                                }}>
                                    {book.keterangan}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Related Books */}
                {relatedBooks.length > 0 && (
                    <div style={{ marginTop: '40px' }}>
                        <h2 style={{
                            fontSize: isMobile ? '18px' : '22px',
                            fontWeight: 'bold',
                            marginBottom: '20px',
                            color: '#333'
                        }}>
                            Buku Terkait
                        </h2>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                            gap: isMobile ? '12px' : '16px'
                        }}>
                            {relatedBooks.map((relatedBook) => (
                                <div
                                    key={relatedBook.id}
                                    onClick={() => navigate(`/user/book/${relatedBook.id}`)}
                                    style={{
                                        backgroundColor: '#fff',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        transition: 'all 0.3s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-5px)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                    }}
                                >
                                    <div style={{
                                        height: isMobile ? '120px' : '150px',
                                        backgroundColor: '#f5f5f5',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {relatedBook.foto_buku ? (
                                            <img
                                               src={`${process.env.REACT_APP_BASE_URL}/images/buku/${relatedBook.foto_buku}`}
                                                alt={relatedBook.judul}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        ) : (
                                            <FaBook style={{ fontSize: '40px', color: '#ccc' }} />
                                        )}
                                    </div>
                                    <div style={{ padding: isMobile ? '10px' : '12px' }}>
                                        <h4 style={{
                                            margin: '0 0 5px 0',
                                            fontSize: isMobile ? '12px' : '14px',
                                            fontWeight: 'bold',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {relatedBook.judul}
                                        </h4>
                                        <p style={{
                                            margin: 0,
                                            fontSize: isMobile ? '11px' : '12px',
                                            color: '#666',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {relatedBook.pengarang || 'Penulis tidak diketahui'}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Modal Peminjaman */}
            {isModalOpen && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 99999,
                        padding: isMobile ? '20px' : '40px'
                    }}
                    onClick={closeModal}
                >
                    <div 
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            width: '100%',
                            maxWidth: '500px',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Content sama seperti di BookCard */}
                        <div style={{
                            padding: isMobile ? '15px 20px' : '20px 24px',
                            borderBottom: '1px solid #eee',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: isMobile ? '1.2rem' : '1.4rem',
                                fontWeight: 'bold'
                            }}>
                                Ajukan Peminjaman Buku
                            </h3>
                            <button
                                onClick={closeModal}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    padding: '5px'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ padding: isMobile ? '20px' : '24px' }}>
                            <div style={{
                                backgroundColor: '#e7f3ff',
                                border: '1px solid #b3d9ff',
                                borderRadius: '6px',
                                padding: '12px',
                                marginBottom: '20px',
                                fontSize: '13px'
                            }}>
                                <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#0066cc' }}>
                                    ℹ️ Informasi Penting:
                                </p>
                                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                    <li>Pengajuan memerlukan persetujuan admin</li>
                                    <li>Maksimal menunggu 24 jam</li>
                                    <li>Limit 3 buku per hari</li>
                                    <li>Default peminjaman 7 hari</li>
                                </ul>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '6px',
                                        fontWeight: '600',
                                        fontSize: '14px'
                                    }}>
                                        Judul Buku
                                    </label>
                                    <input
                                        type="text"
                                        value={book.judul}
                                        disabled
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            backgroundColor: '#f5f5f5',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '6px',
                                        fontWeight: '600',
                                        fontSize: '14px'
                                    }}>
                                        Tanggal Peminjaman
                                    </label>
                                    <input
                                        type="date"
                                        name="tanggal_peminjaman"
                                        value={formData.tanggal_peminjaman}
                                        onChange={handleChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '24px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '6px',
                                        fontWeight: '600',
                                        fontSize: '14px'
                                    }}>
                                        Tanggal Kembali
                                    </label>
                                    <input
                                        type="date"
                                        name="tanggal_kembali"
                                        value={formData.tanggal_kembali}
                                        onChange={handleChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        backgroundColor: '#3273dc',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        fontSize: '16px'
                                    }}
                                >
                                    Ajukan Peminjaman
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </LayoutUser>
    );
};

export default BookDetail;